/**
 * The aircraft's JSA performance figures kept in the aircraft register (MB's decision 863, Q768 6 B, 28 Oct 2026;
 * docs/specs/jsa.md §2.1): the empty weight in survey configuration, the maximum take-off weight (MTOW) and the reserve
 * minutes. They do not change from job to job, so they are kept once per aircraft (Teslyn Cloud, Aircraft; Teslyn
 * Field, Project settings), filled into each JSA's performance planning (A, F and H), and can still be changed on a
 * JSA, which then says where its figure differs from the register.
 *
 * TJR kept them per registration in its settings file (`tjf.dat` record 005, `BEW|MTOW|max fuel|L/h|airspeed|reserve|
 * tip tank`); the aircraft imported from TJR's files carry them in `legacy_json.tjr_defaults` (tjr-import.ts), and
 * {@link aircraftPerformanceFromTjr} reads them from there.
 */
import type { JsaAircraft, JsaDraft, JsaPerformanceInput } from './jsa.js';
import type { Aircraft, ValidationIssue } from './model.js';

export type AircraftPerformanceKey = 'empty_weight_kg' | 'mtow_kg' | 'reserve_min';

/** The three figures, their words and units, in the JSA's order (A, F, H). */
export const AIRCRAFT_PERFORMANCE: readonly {
  readonly key: AircraftPerformanceKey;
  readonly label: string;
  readonly unit: string;
}[] = [
  { key: 'empty_weight_kg', label: 'Empty weight', unit: 'kg' },
  { key: 'mtow_kg', label: 'MTOW', unit: 'kg' },
  { key: 'reserve_min', label: 'Reserve', unit: 'min' },
];

/** The longest reserve a JSA takes (`checkJsa`), minutes. */
export const AIRCRAFT_RESERVE_MAX_MIN = 240;

type Figures = Partial<Pick<Aircraft, AircraftPerformanceKey>>;

/**
 * The register's checks of the three figures (Teslyn Cloud's `aircraft` checks and Teslyn Field's database are the
 * same): the weights more than 0 kg, the empty weight below the MTOW, the reserve 0 to 240 minutes.
 */
export function validateAircraftPerformance(a: Figures): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const e = a.empty_weight_kg ?? null;
  const m = a.mtow_kg ?? null;
  const r = a.reserve_min ?? null;
  if (e !== null && !(Number.isFinite(e) && e > 0))
    issues.push({ field: 'empty_weight_kg', message: 'Empty weight must be more than 0 kg.' });
  if (m !== null && !(Number.isFinite(m) && m > 0))
    issues.push({ field: 'mtow_kg', message: 'MTOW must be more than 0 kg.' });
  if (e !== null && m !== null && e > 0 && m > 0 && !(e < m))
    issues.push({ field: 'empty_weight_kg', message: 'Empty weight must be less than the MTOW.' });
  if (r !== null && !(Number.isFinite(r) && r >= 0 && r <= AIRCRAFT_RESERVE_MAX_MIN))
    issues.push({
      field: 'reserve_min',
      message: `Reserve must be 0 to ${AIRCRAFT_RESERVE_MAX_MIN} minutes.`,
    });
  return issues;
}

const positive = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;

/**
 * The figures TJR kept for the aircraft (`legacy_json.tjr_defaults` of an aircraft imported from TJR's files: its
 * `tjf.dat` 005 BEW, MTOW and reserve), each null when TJR had none. Never guessed.
 */
export function aircraftPerformanceFromTjr(
  a: Pick<Aircraft, 'legacy_json'>,
): Record<AircraftPerformanceKey, number | null> {
  const d = (a.legacy_json ?? {})['tjr_defaults'];
  const o = d && typeof d === 'object' && !Array.isArray(d) ? (d as Record<string, unknown>) : {};
  const reserve = o['reserve_min'];
  const out = {
    empty_weight_kg: positive(o['bew_kg']),
    mtow_kg: positive(o['mtow_kg']),
    reserve_min:
      typeof reserve === 'number' &&
      Number.isFinite(reserve) &&
      reserve >= 0 &&
      reserve <= AIRCRAFT_RESERVE_MAX_MIN
        ? reserve
        : null,
  };
  // a pair the register would refuse (the empty weight not below the MTOW) is not brought in
  if (out.empty_weight_kg !== null && out.mtow_kg !== null && !(out.empty_weight_kg < out.mtow_kg))
    return { ...out, empty_weight_kg: null, mtow_kg: null };
  return out;
}

/** The register's figures of the aircraft (null each when not set). */
export function aircraftPerformance(
  a: Figures | null | undefined,
): Record<AircraftPerformanceKey, number | null> {
  return {
    empty_weight_kg: a?.empty_weight_kg ?? null,
    mtow_kg: a?.mtow_kg ?? null,
    reserve_min: a?.reserve_min ?? null,
  };
}

/** A JSA aircraft row's performance with the register's figures where the row has none. */
function filled(p: JsaPerformanceInput, a: Figures | null | undefined): JsaPerformanceInput {
  const reg = aircraftPerformance(a);
  return {
    ...p,
    empty_weight_kg: p.empty_weight_kg ?? reg.empty_weight_kg,
    mtow_kg: p.mtow_kg ?? reg.mtow_kg,
    reserve_min: p.reserve_min ?? reg.reserve_min,
  };
}

/**
 * The JSA with the register's empty weight, MTOW and reserve filled into each aircraft row that has none (decision
 * 863: "filled in on every JSA"); a figure typed on the JSA is kept. The same object when nothing is filled.
 */
export function jsaFillFromRegister(
  d: JsaDraft,
  aircraft: readonly (Pick<Aircraft, 'id'> & Figures)[],
): JsaDraft {
  let changed = false;
  const rows = d.aircraft.map((row) => {
    const p = filled(
      row.performance,
      aircraft.find((a) => a.id === row.aircraft_id),
    );
    if (
      p.empty_weight_kg === row.performance.empty_weight_kg &&
      p.mtow_kg === row.performance.mtow_kg &&
      p.reserve_min === row.performance.reserve_min
    )
      return row;
    changed = true;
    return { ...row, performance: p };
  });
  return changed ? { ...d, aircraft: rows } : d;
}

/** A JSA figure that is not the register's. */
export interface JsaRegisterDifference {
  readonly key: AircraftPerformanceKey;
  readonly jsa: number;
  readonly register: number;
  /** In words: "F) MTOW is 4100 kg on this JSA; the aircraft register has 4110 kg." */
  readonly words: string;
}

const LETTER: Readonly<Record<AircraftPerformanceKey, string>> = {
  empty_weight_kg: 'A) Empty weight',
  mtow_kg: 'F) MTOW',
  reserve_min: 'H) Reserve',
};

/**
 * Where a JSA aircraft row's empty weight, MTOW or reserve differs from the aircraft register (both set), in words, so
 * the screen can say so beside the figure.
 */
export function jsaRegisterDifferences(
  row: Pick<JsaAircraft, 'performance'>,
  a: Figures | null | undefined,
): JsaRegisterDifference[] {
  const reg = aircraftPerformance(a);
  const out: JsaRegisterDifference[] = [];
  for (const f of AIRCRAFT_PERFORMANCE) {
    const jsa = row.performance[f.key];
    const register = reg[f.key];
    if (jsa === null || register === null || Math.abs(jsa - register) < 1e-9) continue;
    out.push({
      key: f.key,
      jsa,
      register,
      words: `${LETTER[f.key]} is ${jsa} ${f.unit} on this JSA; the aircraft register has ${register} ${f.unit}.`,
    });
  }
  return out;
}
